import React, { useState, useRef, useContext, useEffect } from "react";
import { useDrag } from "react-dnd";
import Plot from "react-plotly.js";
import { DashboardContext } from "../../context/DashboardContext";
import { GraphContext } from "../../context/GraphContext";
import { WebSocketContext } from "../../context/WebSocketContext";
import { useTutorialProgress } from "../../context/TutorialContext";
import {
  getColor,
  isDeepEqual,
  getRepresentatives,
  generateWarningObject,
  hasProperty,
  getShortName,
  getTextWithTooltip,
  useInputSize,
  findMinMaxY,
  downloadStringTxtFile,
  normalToLog,
  logToNormal,
  isInverseUnit,
  getQuantityNameFromModels,
  jsxToHtmlString,
  convertDataXYFromUnitsToUnits,
  convertDataFromUnitsToUnits,
  convertRangeRequestFromUnitsToCm1,
  getQuantityXUnitFromModels,
} from "../../utils/helpers";
import {
  deepCopy,
  getModelById,
  getModelsByIds,
  replaceModelById,
} from "../leftSide/Models/modelLogic";
import units from "../../utils/units";
// import { Button } from "@mui/material";
import {
  createModelDistPayload,
  createModelList,
  createGraphModelEntry,
  generatePlotData,
  requestSingleModelCurve,
} from "./graphLogic";
import "./middle.scss";
import { RAMAN_REF_ID } from "../../utils/constants";
//import DOMPurify from "dompurify";
import AutoFitModal from "../commonComponents/AutoFitModal";
import ConfirmationScreen from "../commonComponents/ConfirmationScreen";
import OptionsModal from "./WindowComponents/OptionsModal";
// ICONS ICONS ICONS BELOW
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import SettingsIcon from "@mui/icons-material/Settings";
// import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { ResizableBox } from "react-resizable";
import "react-resizable/css/styles.css";
import { debounce } from "lodash";
import graphPlaceholder from "../../res/images/graph_placeholder.png";
import { FileHandlingContext } from "../../context/FileHandlingContext";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import CurvesModal from "./WindowComponents/CurvesModal";
import ExportCurvesModal from "./WindowComponents/ExportCurvesModal";
// import { processFileWithOptions } from "../leftSide/Files/fileProcessing/processingLogic";
import { GeneralContext } from "../../context/GeneralContext";
import { AuthContext } from "../../context/AuthContext";
import CloseButton from "../commonComponents/CloseButton";
import CustomButton from "../commonComponents/CustomButton";

function DraggableGraphWindow(props) {
  const {
    id,
    initialPosition,
    initialLayout,
    initialData,
    initialSize,
    onDragStart,
    onDragEnd,
    zIndex,
    title,
    deleteGraph,
    onGraphClick,
    type,
    initialGraph,
  } = props;
  const {
    uploadedFiles,
    modelData,
    setModelData,
    setWarnings,
    setNewWarningCount,
    parametersOpen,
    setParametersOpen,
    setCallFromGraph,
    setFileCallFromGraph,
    allLocalModels,
  } = useContext(DashboardContext);
  const { graphs, setGraphs } = useContext(GraphContext);
  const { limitedToast, recordedErrorLog } = useContext(GeneralContext);
  const { currentUser, checkAccessLevel } = useContext(AuthContext);
  const { monitorStateChange } = useTutorialProgress();
  const { setSelectedFiles } = useContext(FileHandlingContext);
  // STATES:
  const [localTitle, setLocalTitle] = useState(title);
  const [position, setPosition] = useState(initialPosition);
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    lastJsonMessage,
    sendJsonMessage,
    isFitOngoing,
    isLoopOngoing,
    retrieveRequestData,
    deleteRequestDataEntry,
  } = useContext(WebSocketContext);
  const [delModalIsOpen, setDelModalIsOpen] = useState(false);
  const [selectFileList, setSelectFileList] = useState([]);
  const [selectModelList, setSelectModelList] = useState([]);
  const [graphData, setGraphData] = useState(initialData);
  const [graphLayout, setGraphLayout] = useState(initialLayout);
  const [graphLayoutForDisplay, setGraphLayoutForDisplay] =
    useState(initialLayout);
  const [graphDataForDisplay, setGraphDataForDisplay] = useState(initialData);
  const [distList, setDistList] = useState([]);

  // REFS:
  const windowDataIcons = useRef();
  const windowIcons = useRef();
  const graphRef = useRef(graphs);
  const fileRef = useRef(uploadedFiles);
  const modelDataRef = useRef(deepCopy(modelData));
  const graphsUpdatedFromCurves = useRef(null);

  const [currentSize, setCurrentSize] = useState(initialSize);
  const justResized = useRef(false);
  const [minimisedWidth, setMinimisedWidth] = useState(50);

  // Graph settings modal variables:
  const [settingsModalIsOpen, setSettingsModalIsOpen] = useState(false);
  // -- Setting initial axis names for editing functionality and also sanitizing them from HTML elements
  // -- such as - <sup>
  // Curves modal variables:
  const [curvesModalIsOpen, setCurvesModalIsOpen] = useState();

  // Export modal variables:
  const [exportModalIsOpen, setExportModalIsOpen] = useState(false);

  //Variables for Extra Section (for now just Raman AutoFit):
  // const [extraSection, setExtraSection] = useState(true);
  const [extraRaman, setExtraRaman] = useState(false);

  // Variables for AutoFit:
  const [autofitModalIsOpen, setAutofitModalIsOpen] = useState(false);
  const autoFitRef = useRef();
  const [ramanInGraph, setRamanInGraph] = useState(null);
  const [autoSelectedFile, setAutoSelectedFile] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [xAxisUpdatedInOptions, setXAxisUpdatedInOptions] = useState(false);

  const getGraphLayout = (layout) => {
    try {
      let newLayout = deepCopy(layout);
      const hideUnitsX = layout.xaxis.hideUnits || false;
      const hideUnitsY = layout.yaxis.hideUnits || false;
      let unitX = layout.xaxis.unit || units[0];
      let unitY = "";
      if (
        hasProperty(layout.yaxis, "multiplyBy100") &&
        layout.yaxis.multiplyBy100
      ) {
        unitY = "%";
      }
      let rangeX = layout.xaxis.range;
      let rangeY = layout.yaxis.range;
      if (layout.xaxis.type === "log") {
        if (rangeX[0] <= 0) {
          rangeX[0] = 0.01;
        }
        if (rangeX[1] <= rangeX[0]) {
          rangeX[1] = 0.02;
        }
        rangeX = rangeX.map((value) => normalToLog(value));
      }
      if (layout.yaxis.type === "log") {
        if (rangeY[0] <= 0) {
          rangeY[0] = 0.01;
        }
        if (rangeY[1] <= rangeY[0]) {
          rangeY[1] = 0.02;
        }
        rangeY = rangeY.map((value) => normalToLog(value));
      }
      let unitXName = unitX.value;
      if (unitXName === "cm^-1") {
        unitXName = "cm<sup>-1</sup>";
      }
      newLayout.xaxis.title.text =
        layout.xaxis.title.text +
        (hideUnitsX || layout.xaxis.title.text.includes("cm<sup>-1</sup>")
          ? ""
          : " [" + unitXName + "]");
      newLayout.yaxis.title.text =
        layout.yaxis.title.text +
        (hideUnitsY || unitY === "" ? "" : " [" + unitY + "]");
      newLayout.xaxis.range = rangeX;
      newLayout.yaxis.range = rangeY;
      return newLayout;
    } catch (error) {
      recordedErrorLog("Error getting graph layout:", error);
      return layout;
    }
  };

  const getGraphData = (dataInput, unitX, multiplyBy100) => {
    try {
      let dataOutput = deepCopy(dataInput);
      for (let i = 0; i < dataOutput.length; i++) {
        let convertedData = convertDataXYFromUnitsToUnits(
          units[0],
          unitX,
          dataOutput[i].x,
          dataOutput[i].y,
          true
        );
        dataOutput[i].x = convertedData[0];
        dataOutput[i].y = convertedData[1];
        if (multiplyBy100) {
          for (let j = 0; j < dataOutput[i].y.length; j++) {
            dataOutput[i].y[j] = dataOutput[i].y[j] * 100;
          }
        }
      }
      return dataOutput;
    } catch (error) {
      recordedErrorLog("Error getting graph data:", error);
      return dataInput;
    }
  };

  useEffect(() => {
    setGraphLayoutForDisplay(getGraphLayout(graphLayout));
    setGraphDataForDisplay(
      getGraphData(
        graphData,
        graphLayout.xaxis.unit || units[0],
        graphLayout.yaxis.multiplyBy100 || false
      )
    );
  }, [graphLayout, graphData]);

  useEffect(() => {
    if (initialLayout && !isDeepEqual(initialLayout, graphLayout)) {
      setGraphLayout(initialLayout);
    }
  }, [initialLayout]);

  useEffect(() => {
    if (initialPosition && !isDeepEqual(initialPosition, position)) {
      setPosition(initialPosition);
    }
  }, [initialPosition]);

  useEffect(() => {
    if (
      initialSize &&
      hasProperty(initialSize, "width") &&
      !isDeepEqual(initialSize, currentSize)
    ) {
      setCurrentSize(initialSize);
    }
  }, [initialSize]);

  useEffect(() => {
    if (type === "loop-variable-tracking") {
      setGraphLayout(initialGraph.layout);
    }
  }, [initialGraph]);

  useEffect(() => {
    return () => {
      debouncedSetSize.cancel();
    };
  }, []);

  // We are using useEffect here to create onClick listeners because plotly doesn't provide easier way to achieve this
  useEffect(() => {
    const addClickHandlers = () => {
      const xTitleText = document.querySelector(".g-xtitle text.xtitle");
      const yTitleText = document.querySelector(".g-ytitle text.ytitle");

      if (xTitleText) {
        xTitleText.style.pointerEvents = "auto"; // Ensure pointer events are enabled
        xTitleText.onclick = () => {
          console.log("X CLICKED");
        };
      }

      if (yTitleText) {
        yTitleText.style.pointerEvents = "auto";
        yTitleText.onclick = () => {
          console.log("Y CLICKED");
        };
      }
    };

    // Calling the function after a short delay to ensure the elements exist
    const timeoutId = setTimeout(addClickHandlers, 500);
    return () => clearTimeout(timeoutId); // Cleanup timeout on component unmount
  }, []);

  useEffect(() => {
    try {
      if (xAxisUpdatedInOptions) {
        if (
          graphData.length > 0 &&
          graphData.some((dataEntry) => hasProperty(dataEntry, "modelId"))
        ) {
          handleRangesUpdate(graphLayout);
        }

        setXAxisUpdatedInOptions(false);
      }
    } catch (error) {
      recordedErrorLog("Error updating x axis in options:", error);
    }
  }, [xAxisUpdatedInOptions]);

  // We call the cleanup only when the graphs got updated with latest ranges.
  useEffect(() => {
    if (graphsUpdatedFromCurves.current) {
      graphsUpdatedFromCurves.current = false;
    }
  }, [graphs]);

  useEffect(() => {
    try {
      const countOfFiles = distList.filter((dist) =>
        Object.prototype.hasOwnProperty.call(dist, "fileId")
      );
      if (countOfFiles.length === 1) {
        setAutoSelectedFile(
          uploadedFiles.find((file) => file.ID === countOfFiles[0].fileId)
        );
      } else {
        setAutoSelectedFile(null);
      }
    } catch (error) {
      recordedErrorLog("Error getting auto selected file:", error);
    }
  }, [distList]);

  useEffect(() => {
    try {
      graphRef.current = graphs;
      const currentGraph = graphRef.current.find((graph) => graph.id == id);
      if (
        currentGraph != undefined &&
        !isDeepEqual(currentGraph.plotData, graphData)
      ) {
        needsExtra(currentGraph.containedModels);
        setGraphData([...currentGraph.plotData]);

        // THIS IS DEMO ONLY, WILL PROBABLY MESS THINGS UP WITH GRAPH NAVIGATION FOR NON DEMO STUFF
        // BECAUSE LOCAL CHANGES SHOULD BE A PRIORITY
        // setGraphLayout(currentGraph.layout);
      }
    } catch (error) {
      recordedErrorLog("Error setting graph data and needs extra:", error);
    }
  }, [graphs]);

  useEffect(() => {
    try {
      setDistList(getRepresentatives(graphData));
      const currentGraph = graphRef.current.find((graph) => graph.id == id);
      needsExtra(currentGraph.containedModels);
    } catch (error) {
      recordedErrorLog("Error setting dist list and needs extra:", error);
    }
  }, [graphData]);

  useEffect(() => {
    try {
      const updatedGraphs = graphRef.current.map((graph) => {
        if (graph.id === id) {
          return { ...graph, position: { ...position } };
        } else return graph;
      });

      setGraphs(updatedGraphs);
    } catch (error) {
      recordedErrorLog("Error setting graph positions:", error);
    }
  }, [position]);

  const changeRamanInGraphModel = (modelToUse, ID) => {
    try {
      setRamanInGraph(modelToUse);

      const copyOfModelData = deepCopy(modelData);
      const updatedModelData = replaceModelById(
        copyOfModelData,
        modelToUse,
        ID
      );

      setModelData(updatedModelData);
    } catch (error) {
      recordedErrorLog("Error changing raman in graph model:", error);
    }
  };

  function needsExtra(modelList) {
    try {
      if (modelList.length >= 1) {
        const idList = modelList.map((modelQuantity) => modelQuantity.modelId);
        let modelsInGraph = getModelsByIds(idList, modelData);
        let foundRaman = false;

        for (let i = 0; i < modelsInGraph.length; i++) {
          const model = modelsInGraph[i];
          if (model.speqqleID === RAMAN_REF_ID) {
            foundRaman = true;
            setRamanInGraph(model);
          }
        }

        setExtraRaman(foundRaman);

        // Check if anything is found here - update if when more extras are added
        if (!foundRaman) {
          setExtrasFalse();
        } else {
          // setExtraSection(true);
        }
      } else {
        // set all extras here to false
        setExtrasFalse();
      }
    } catch (error) {
      recordedErrorLog("Error setting extras false:", error);
    }
  }

  function setExtrasFalse() {
    // setExtraSection(false);
    setExtraRaman(false);
  }

  const [, drag, preview] = useDrag(() => ({
    type: "WINDOW",
    item: { id },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    canDrag: () => {
      // if (!isDraggingRef.current) {
      //   // console.log("id sent: ", id);
      //   // onDragStart(id);
      //   isDraggingRef.current = true;
      // }
      return true;
    },
    end: (item, monitor) => {
      const delta = monitor.getDifferenceFromInitialOffset();
      setPosition((prevPosition) => {
        const newY =
          prevPosition.y + delta.y < -60 ? -60 : prevPosition.y + delta.y;
        return {
          x: prevPosition.x + delta.x,
          y: newY,
        };
      });
      onDragEnd();
      // isDraggingRef.current = false;
    },
  }));

  function handleNewModelCurve(jsonMessage, details) {
    try {
      const foundData = jsonMessage.Model.SendCurve.find(
        (curveEntry) =>
          curveEntry.modelid == details.requestDetails.modelId &&
          curveEntry.curves[0].quantity == details.requestDetails.quantity
      );

      if (foundData != undefined) {
        handleModelDistAdd(foundData);
      } else {
        console.log("DATA NOT FOUND FOR MODEL CURVE");
      }
    } catch (error) {
      recordedErrorLog(
        "Error handling new model curve response in DraggableGraphWindow: ",
        error,
        true,
        "Failed to obtain model curve.",
        {
          jsonMessage: jsonMessage,
          details: details,
        }
      );
    }
  }

  function handleModelCurveUpdate(jsonMessage, details) {
    try {
      if (details.graphId === id) {
        const dataToUse = [];

        for (let i = 0; i < details.modelsToUpdate.length; i++) {
          const requestedModelQuantSet = details.modelsToUpdate[i];

          const foundData = jsonMessage.Model.SendCurve.find(
            (curve) =>
              curve.modelid == requestedModelQuantSet.modelId &&
              curve.curves[0].quantity == requestedModelQuantSet.quantity
          );

          dataToUse.push(foundData);
        }

        handleGraphModelUpdate(dataToUse);
      }
    } catch (error) {
      recordedErrorLog("Error handling model curve update response: ", error);
    }
  }

  useEffect(() => {
    try {
      if (lastJsonMessage) {
        const processingJsonMessage = deepCopy(lastJsonMessage);
   
        if (hasProperty(processingJsonMessage, "requestID")) {
          const requestData = retrieveRequestData(processingJsonMessage.requestID);
          if (requestData !== null) {
            switch (requestData.type) {
              case "new-model-curve-request":
                if (requestData.graphId === id) {
                  if (
                    !hasProperty(processingJsonMessage, "Model") ||
                    !hasProperty(processingJsonMessage.Model, "SendCurve") ||
                    processingJsonMessage.Model.SendCurve.length === 0 ||
                    processingJsonMessage.Model.SendCurve[0] === undefined ||
                    processingJsonMessage.Model.SendCurve[0] === null
                  ) {
                    if (!hasProperty(processingJsonMessage, "Error")) {
                      deleteRequestDataEntry(processingJsonMessage.requestID);
                      recordedErrorLog(
                        "No graph content found in the json message without error.",
                        null,
                        true,
                        "Failed to obtain model curve.",
                        {
                          jsonMessage: processingJsonMessage,
                          requestData: requestData,
                        }
                      );
                    }
                  } else {
                    handleNewModelCurve(processingJsonMessage, requestData);
                  }
                  deleteRequestDataEntry(processingJsonMessage.requestID);
                }
                break;
              case "model-curve-update":
                if (requestData.graphId === id) {
                  if (
                    !hasProperty(processingJsonMessage, "Model") ||
                    !hasProperty(processingJsonMessage.Model, "SendCurve") ||
                    processingJsonMessage.Model.SendCurve.length === 0 ||
                    processingJsonMessage.Model.SendCurve[0] === undefined ||
                    processingJsonMessage.Model.SendCurve[0] === null
                  ) {
                    if (!hasProperty(processingJsonMessage, "Error")) {
                      deleteRequestDataEntry(processingJsonMessage.requestID);
                      recordedErrorLog(
                        "No graph content found in the json message without error.",
                        null,
                        true,
                        "Failed to obtain model curve.",
                        {
                          jsonMessage: processingJsonMessage,
                          requestData: requestData,
                        }
                      );
                    }
                  } else {
                    handleModelCurveUpdate(processingJsonMessage, requestData);
                  }
                  deleteRequestDataEntry(processingJsonMessage.requestID);
                }
                break;
              case "export-model-curve-request":
                if (requestData.graphId === id) {
                  if (
                    !hasProperty(processingJsonMessage, "Model") ||
                    !hasProperty(processingJsonMessage.Model, "SendCurve") ||
                    processingJsonMessage.Model.SendCurve.length === 0 ||
                    processingJsonMessage.Model.SendCurve[0] === undefined ||
                    processingJsonMessage.Model.SendCurve[0] === null
                  ) {
                    if (!hasProperty(processingJsonMessage, "Error")) {
                      deleteRequestDataEntry(processingJsonMessage.requestID);
                      recordedErrorLog(
                        "No graph content found in the json message without error for export.",
                        null,
                        true,
                        "Failed to obtain model curve.",
                        {
                          jsonMessage: processingJsonMessage,
                          requestData: requestData,
                        }
                      );
                    }
                  } else {
                    handleExportModelCurveResponse(
                      processingJsonMessage,
                      requestData
                    );
                  }
                  deleteRequestDataEntry(processingJsonMessage.requestID);
                }
                break;
              default:
                break;
            }
          }
        } else {
          throw new Error("Request ID was not attached to the json message.");
        }
      }
    } catch (error) {
      recordedErrorLog(
        "Error in model curve useEffect",
        error,
        true,
        "Interface Error.",
        {
          lastJsonMessage: lastJsonMessage,
        }
      );
    }
  }, [lastJsonMessage]);

  const handleModelDistAdd = (data) => {
    try {
      const { modelid, curves } = data;
      const coordinates = curves[0].coordinates;
      const quantity = curves[0].quantity;

      const currentGraph = graphRef.current.find((graph) => graph.id == id);
      const modelAdded = getModelById(modelid, modelData);
      const graphColor = getColor(currentGraph.plotData.length + 1);

      const shortName = getShortName(modelAdded.displayName);

      const xUnit = getQuantityXUnitFromModels([modelAdded], modelid, quantity);

      const plotData = generatePlotData(
        coordinates,
        "",
        "",
        graphColor,
        shortName,
        modelAdded.displayName,
        null,
        modelAdded.FE_ID,
        null,
        quantity,
        modelData
      );

      if (plotData.length !== 0) {
        currentGraph.plotData = [...currentGraph.plotData, ...plotData];
        currentGraph.containedModels = [
          ...currentGraph.containedModels,
          {
            modelId: modelAdded.FE_ID,
            quantity: quantity,
            originalXUnit: xUnit,
          },
        ];
      } else {
        limitedToast(
          "Model curve is empty and will not be added to the graph."
        );
        generateWarningObject(
          "Model curve is empty and will not be added to the graph.",
          1,
          setWarnings,
          setNewWarningCount
        );
      }

      // const size = expandedRef.current ? largeWindow : smallWindow;
      const size = currentSize;

      let localGraphs = deepCopy(graphRef.current);

      localGraphs = localGraphs.map((graph) => {
        if (graph.id == id) {
          return currentGraph;
        } else {
          return graph;
        }
      });

      setGraphs(localGraphs);

      // deepCopy is needed here, because the <Plot /> doesn't catch that the data is changed otherwise - probably
      // reference remains the same. This happened because there is now 'transition' added to plot layout
      setGraphData(deepCopy(currentGraph.plotData));
      setGraphLayout({
        ...currentGraph.layout,
        autosize: true,
        width: size.width - 10,
        height: size.height - 40,
      });
    } catch (error) {
      recordedErrorLog("model curve add handle failure: ", error);
    }
  };

  const handleGraphModelUpdate = (data) => {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id == id);

      for (let i = 0; i < data.length; i++) {
        if (!data[i]) {
          continue; // Skip undefined/null elements
        }
        const { modelid, curves } = data[i];
        const coordinates = curves[0].coordinates;
        const quantity = curves[0].quantity;

        let currentPlotData = {};
        const otherPlotData = [];

        for (let j = 0; j < currentGraph.plotData.length; j++) {
          const plot = currentGraph.plotData[j];

          if (
            hasProperty(plot, "modelId") &&
            plot.modelId === modelid &&
            plot.quantity === quantity
          ) {
            currentPlotData = plot;
          } else {
            otherPlotData.push(plot);
          }
        }

        const xUnit = getQuantityXUnitFromModels(modelData, modelid, quantity);
        const convertedData = convertDataFromUnitsToUnits(xUnit, units[0], coordinates, true);

        const newPlotData = {
          ...currentPlotData,
          x: convertedData.map((point) => point.x),
          y: convertedData.map((point) => point.y),
        };

        currentGraph.plotData = [...otherPlotData, newPlotData];
      }

      let localGraphs = deepCopy(graphRef.current);

      currentGraph.layout = graphLayout;

      localGraphs = localGraphs.map((graph) => {
        if (graph.id == id) {
          return currentGraph;
        } else {
          return graph;
        }
      });

      setGraphs(localGraphs);

      setGraphData(deepCopy(currentGraph.plotData));

      graphsUpdatedFromCurves.current = true;
    } catch (error) {
      recordedErrorLog("Graph model update handler failure: ", error);
    }
  };

  function handleModelClick(modelId, quantity) {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id === id);
      if (
        currentGraph !== undefined &&
        !currentGraph.containedModels.some(
          (modelQuantPair) =>
            modelId === modelQuantPair.modelId &&
            modelQuantPair.quantity === quantity
        )
      ) {
        const foundModel = getModelById(modelId, modelData);

        let minValueRequest = currentGraph.layout.xaxis.range[0];
        let maxValueRequest = currentGraph.layout.xaxis.range[1];
        let unitX = currentGraph.layout.xaxis.unit || units[0];
        const originalXUnit = getQuantityXUnitFromModels(modelData, modelId, quantity);

        if (
          currentGraph.containedFiles.length === 0 &&
          currentGraph.containedModels.length === 0 &&
          (!hasProperty(currentGraph.layout.xaxis, "autorange") ||
            currentGraph.layout.xaxis.autorange === true) &&
          (!hasProperty(currentGraph.layout.xaxis, "unit") ||
            currentGraph.layout.xaxis.unit === units[0])
        ) {
          unitX = originalXUnit;
          const defaultModel = allLocalModels.find(
            (defMod) => defMod.speqqle_id === foundModel.speqqleID
          );

          if (defaultModel !== undefined) {
            minValueRequest = defaultModel.default_curve_min;
            maxValueRequest = defaultModel.default_curve_max;

            setGraphLayout((old) => {
              return {
                ...old,
                xaxis: {
                  ...old.xaxis,
                  range: [minValueRequest, maxValueRequest],
                  unit: unitX,
                },
              };
            });

            setGraphs((oldGraphs) => {
              return oldGraphs.map((graphOld) => {
                if (graphOld.id === id) {
                  graphOld.layout = {
                    ...graphOld.layout,
                    xaxis: {
                      ...graphOld.layout.xaxis,
                      range: [minValueRequest, maxValueRequest],
                      unit: unitX,
                    },
                  };
                }
                return graphOld;
              });
            });
          }
        }

        let isLogarithmic = false;
        if (hasProperty(currentGraph.layout.xaxis, "type")) {
          isLogarithmic = currentGraph.layout.xaxis.type === "log";
        }

        // const potentialCurve =
        requestSingleModelCurve(
          foundModel,
          uploadedFiles,
          sendJsonMessage,
          minValueRequest,
          maxValueRequest,
          isLogarithmic,
          unitX,
          quantity,
          currentUser,
          { type: "new-model-curve-request", graphId: id },
          originalXUnit
        );
        monitorStateChange("model_curve_added_to_graph", {
          model: foundModel,
          modelName: foundModel.displayName,
          modeltype: foundModel.speqqleID,
          quantity: quantity,
          currentGraph: currentGraph,
          graphs: graphRef.current,
        });
      } else {
        limitedToast("Graph already contains selected model-quantity pair.");
        generateWarningObject(
          "Graph already contains selected model-quantity pair.",
          1,
          setWarnings,
          setNewWarningCount
        );
      }
    } catch (error) {
      recordedErrorLog("Model click handler failure: ", error);
    }
  }

  useEffect(() => {
    try {
      if (!isDeepEqual(modelDataRef.current, modelData)) {
        modelDataRef.current = deepCopy(modelData);
      }

      if (modelDataRef.current.length > 0) {
        // Get current graph to check which model quantities are already displayed
        const currentGraph = graphRef.current.find((graph) => graph.id == id);
        const displayedModelQuantities =
          currentGraph && currentGraph.plotData
            ? currentGraph.plotData
                .filter((plot) => hasProperty(plot, "modelId"))
                .map((plot) => ({
                  modelId: plot.modelId,
                  quantity: plot.quantity,
                }))
            : null;

        setSelectModelList(
          createModelList(
            handleModelClick,
            modelData,
            createGraphModelEntry,
            displayedModelQuantities
          )
        );
      } else {
        setSelectModelList([]);
      }
    } catch (error) {
      recordedErrorLog("Error updating select model list:", error);
    }
  }, [modelData, graphData, id]);

  useEffect(() => {
    try {
      if (modelData.length > 0) {
        // Get current graph to check which model quantities are already displayed
        const currentGraph = graphRef.current.find((graph) => graph.id == id);
        const displayedModelQuantities =
          currentGraph && currentGraph.plotData
            ? currentGraph.plotData
                .filter((plot) => hasProperty(plot, "modelId"))
                .map((plot) => ({
                  modelId: plot.modelId,
                  quantity: plot.quantity,
                }))
            : null;

        setSelectModelList(
          createModelList(
            handleModelClick,
            modelData,
            createGraphModelEntry,
            displayedModelQuantities
          )
        );
      }
    } catch (error) {
      recordedErrorLog("Error initializing select model list:", error);
    }
  }, []);

  const updateGraphLayout = (newCurrentGraph) => {
    try {
      const currentGraph = newCurrentGraph;
      let localGraphs = deepCopy(graphRef.current);

      localGraphs = localGraphs.map((graph) => {
        if (graph.id == currentGraph.id) {
          return currentGraph;
        } else {
          return graph;
        }
      });
      setGraphs(localGraphs);
      setLocalTitle(currentGraph.title);
      setGraphLayout(currentGraph.layout);
      graphRef.current = localGraphs;
    } catch (error) {
      recordedErrorLog("Graph layout update failure: ", error);
    }
  };

  const updateGraphPlots = (plotsUpdate) => {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id == id);
      let localGraphs = deepCopy(graphRef.current);

      currentGraph.plotData = plotsUpdate;

      localGraphs = localGraphs.map((graph) => {
        if (graph.id == id) {
          return currentGraph;
        } else {
          return graph;
        }
      });

      setGraphs(localGraphs);
      setGraphData(plotsUpdate);
    } catch (error) {
      recordedErrorLog("Graph plot update failure: ", error);
    }
  };

  const handleFileSelected = (selectedId) => {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id == id);
      if (
        currentGraph !== undefined &&
        !currentGraph.containedFiles.some((id) => selectedId === id)
      ) {
        const dataToSet = fileRef.current.find((file) => file.ID == selectedId);
        const data = dataToSet.dataPoints;

        const graphColor = getColor(currentGraph.plotData.length + 1);

        const shortName = getShortName(dataToSet.name);

        const plotData = generatePlotData(
          data,
          dataToSet.dataRangeMin,
          dataToSet.dataRangeMax,
          graphColor,
          shortName,
          dataToSet.name,
          dataToSet.ID,
          null,
          dataToSet.edges,
          modelData
        );

        if (dataToSet.name.includes("Tutorial")) {
          // NEW: Monitor state change for tutorial progression
          monitorStateChange("curve_added_to_graph", {
            curveName: dataToSet.name,
            graphId: id,
            isTutorialData: true,
            currentGraph: currentGraph,
            graphs: graphRef.current,
          });
        }

        if (
          currentGraph.containedFiles.length === 0 &&
          currentGraph.containedModels.length === 0 &&
          (!hasProperty(currentGraph.layout.xaxis, "autorange") ||
            currentGraph.layout.xaxis.autorange === true) &&
          (!hasProperty(currentGraph.layout.xaxis, "unit") ||
            currentGraph.layout.xaxis.unit === units[0])
        ) {
          const yRange = findMinMaxY(data);
          const xUnit = dataToSet.xUnit || units[0];
          let convertedData = data;
          if (xUnit !== units[0]) {
            convertedData = convertDataFromUnitsToUnits(units[0], xUnit, data, true);
          }
          currentGraph.layout.xaxis.range = [
            convertedData[0].x,
            convertedData[convertedData.length - 1].x,
          ];
          currentGraph.layout.xaxis.unit = xUnit;
          currentGraph.layout.yaxis.range = [yRange.minY, yRange.maxY];
        }

        if (plotData.length !== 0) {
          currentGraph.plotData = [...currentGraph.plotData, ...plotData];
          currentGraph.containedFiles = [
            ...currentGraph.containedFiles,
            dataToSet.ID,
          ];
        } else {
          limitedToast(
            "Selected file is empty and will not be added to the graph."
          );
          generateWarningObject(
            "Selected file is empty and will not be added to the graph.",
            1,
            setWarnings,
            setNewWarningCount
          );
        }

        let localGraphs = deepCopy(graphRef.current);

        localGraphs = localGraphs.map((graph) => {
          if (graph.id == id) {
            return currentGraph;
          } else {
            return graph;
          }
        });

        setGraphs(localGraphs);

        // deepCopy is needed here, because the <Plot /> doesn't catch that the data is changed otherwise - probably
        // reference remains the same. This happened because there is now 'transition' added to plot layout
        setGraphData(deepCopy(currentGraph.plotData));
      } else {
        limitedToast("Graph already contains selected file.");
        generateWarningObject(
          "Graph already contains selected file.",
          1,
          setWarnings,
          setNewWarningCount
        );
      }
    } catch (error) {
      recordedErrorLog("Selected file handler failure: ", error);
    }
  };

  useEffect(() => {
    try {
      fileRef.current = uploadedFiles;
      if (uploadedFiles.length > 0) {
        // Get current graph to check which files are already displayed
        const currentGraph = graphRef.current.find((graph) => graph.id == id);
        const displayedFileIds =
          currentGraph && currentGraph.plotData
            ? currentGraph.plotData
                .filter((plot) => hasProperty(plot, "fileId"))
                .map((plot) => plot.fileId)
            : [];

        // Filter out files that are already displayed in the graph
        const availableFiles = uploadedFiles.filter(
          (file) => !displayedFileIds.includes(file.ID)
        );

        const fileList = availableFiles.map((file) => {
          return (
            <div
              key={file.ID}
              className="fileEntry"
              onClick={() => handleFileSelected(file.ID)}
            >
              {getTextWithTooltip(file.name, 40)}
            </div>
          );
        });

        setSelectFileList(fileList);
      }
    } catch (error) {
      recordedErrorLog("Error handling 'uplodedFiles' useEffect hook: ", error);
    }
  }, [uploadedFiles, graphData, id]);

  const updateGraphOnResize = (sizeToUse = null) => {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id == id);

      const newWidth = currentSize.width;
      const newHeight = currentSize.height;

      graphRef.current = graphRef.current.map((graph) => {
        if (graph.id == id) {
          const graphToReturn = {
            ...graph,
            layout: {
              ...currentGraph.layout,
              width: newWidth - 10,
              height: newHeight - 40,
            },
          };

          if (justResized.current) {
            justResized.current = false;

            if (sizeToUse !== null) {
              graphToReturn.size = { ...sizeToUse };
            } else {
              graphToReturn.size = { ...currentSize };
            }
          }

          return graphToReturn;
        } else {
          return graph;
        }
      });

      setGraphs(graphRef.current);

      setGraphLayout({
        ...currentGraph.layout,
        width: newWidth - 10,
        height: newHeight - 40,
      });
    } catch (error) {
      recordedErrorLog("Graph update on resize failure: ", error);
    }
  };

  useEffect(() => {
    updateGraphOnResize();
  }, [currentSize]);

  const debouncedSetSize = debounce((newSize) => {
    setCurrentSize(newSize);
  }, 50); // 100ms debounce time, adjust as needed

  const handleResizeStart = () => {
    if (isExpanded) {
      setIsResizing(true);
    }
  };

  const handleResizeStop = (event, { size }) => {
    setCurrentSize({ width: size.width, height: size.height });
    justResized.current = true;
    updateGraphOnResize(size);
    setTimeout(() => {
      setIsResizing(false);
    }, 10);
  };

  const handleDistDelete = (dist) => {
    try {
      let updatedGraphData = [];
      if (hasProperty(dist, "fileId")) {
        // We are dealing with a file here
        updatedGraphData = graphData.filter((plot) => {
          if (hasProperty(plot, "fileId")) {
            if (plot.fileId === dist.fileId) {
              return false;
            } else {
              return true;
            }
          } else {
            return true;
          }
        });

        setGraphData(updatedGraphData);
      } else {
        // We are dealing with a model here
        updatedGraphData = graphData.filter((plot) => {
          if (hasProperty(plot, "modelId")) {
            if (
              plot.modelId === dist.modelId &&
              plot.quantity === dist.quantity
            ) {
              return false;
            } else {
              return true;
            }
          } else {
            return true;
          }
        });
      }

      setGraphData(updatedGraphData);

      graphRef.current = graphRef.current.map((graph) => {
        if (graph.id == id) {
          if (hasProperty(dist, "fileId")) {
            return {
              ...graph,
              plotData: updatedGraphData,
              containedFiles: updatedGraphData
                .filter((plot) => hasProperty(plot, "fileId"))
                .map((plot) => plot.fileId),
            };
          } else {
            return {
              ...graph,
              plotData: updatedGraphData,
              containedModels: updatedGraphData
                .filter((plot) => hasProperty(plot, "modelId"))
                .map((plot) => {
                  return { modelId: plot.modelId, quantity: plot.quantity, originalXUnit: plot.originalXUnit };
                }),
            };
          }
        } else {
          return graph;
        }
      });

      setGraphs(graphRef.current);
    } catch (error) {
      recordedErrorLog("Curve deletion handler failure: ", error);
    }
  };

  // Settings window functions START VVVVVVVVVVVVVVVVVVVVVV

  const handleSettings = () => {
    try {
      setSettingsModalIsOpen(true);
    } catch (error) {
      recordedErrorLog("Setting handler failure: ", error);
    }
  };

  const handleGraphDelete = () => {
    deleteGraph(id);
    setDelModalIsOpen(false);
  };

  const handleGraphDeleteIcon = () => {
    setDelModalIsOpen(true);
  };

  // Settings window functions END ^^^^^^^^^^^^^^^^^^^^^^^^

  // Curves Window functions START VVVVVVVVVVVVVVVVVVV

  const handleCurvesOptOpen = () => {
    setCurvesModalIsOpen(true);
  };

  // Curves Window functions END ^^^^^^^^^^^^^^^^^^^^^^

  // AutoFit Functionality starts: VVVVVVVVVVVVVVVVV

  const handleRamanAutofit = () => {
    try {
      if (uploadedFiles.length > 0) {
        setAutofitModalIsOpen(true);
      } else {
        limitedToast(
          "There needs to be at least 1 file uploaded for AutoFit to function."
        );
        generateWarningObject(
          "There needs to be at least 1 file uploaded for AutoFit to function.",
          0,
          setWarnings,
          setNewWarningCount
        );
      }
    } catch (error) {
      recordedErrorLog("Error handling raman autofit:", error);
    }
  };

  // AutoFit Functionality ends ^^^^^^^^^^^^^^^^^^^^

  const handleRelayout = (eventData) => {
    try {
      // Extracting new x and y ranges from Plotly event data
      // Note: Plotly returns ranges in the transformed format (display units, possibly log space)
      let xAxisRange = {
        min: graphLayout.xaxis.range[0],
        max: graphLayout.xaxis.range[1],
      };
      let yAxisRange = {
        min: graphLayout.yaxis.range[0],
        max: graphLayout.yaxis.range[1],
      };
      const unitX = graphLayout.xaxis.unit || units[0];
      const isLogx = graphLayout.xaxis.type === "log";
      const isLogy = graphLayout.yaxis.type === "log";
      const isMultiplyBy100 = graphLayout.yaxis.multiplyBy100 || false;

      if (
        hasProperty(eventData, "xaxis.autorange") &&
        eventData["xaxis.autorange"]
      ) {
        monitorStateChange("autoscale_clicked", {
          graphId: id,
        });
        // For autoscale, use the current layout range (already in internal format)
        const currentGraph = deepCopy(
          graphRef.current.find((graph) => graph.id == id)
        );
        if (
          currentGraph &&
          hasProperty(currentGraph, "plotData") &&
          currentGraph.plotData.length > 0
        ) {
          let xmin = null;
          let xmax = null;
          let xminPos = null;
          let yminTotal = null;
          let ymaxTotal = null;
          let ymin = null;
          let ymax = null;

          let convertedPlotData = getGraphData(
            currentGraph.plotData,
            unitX,
            isMultiplyBy100
          );

          for (let i = 0; i < convertedPlotData.length; i++) {
            const plot = deepCopy(convertedPlotData[i]);
            if (
              !hasProperty(plot, "x") ||
              plot.x.length === 0 ||
              !hasProperty(plot, "y") ||
              plot.y.length === 0
            ) {
              continue;
            }

            // get the min and max of the y values in the converted plot data
            let yminTemp = plot.y.length > 0 ? Math.min(...plot.y) : null;
            let ymaxTemp = plot.y.length > 0 ? Math.max(...plot.y) : null;

            // non datafiles should only contribut to "find the lost model curves"
            if (!hasProperty(plot, "fileId")) {
              if (
                yminTemp !== null &&
                (yminTotal === null || yminTemp < yminTotal)
              ) {
                yminTotal = yminTemp;
              }
              if (
                ymaxTemp !== null &&
                (ymaxTotal === null || ymaxTemp > ymaxTotal)
              ) {
                ymaxTotal = ymaxTemp;
              }
              continue;
            }
            // Note: plot.x values in currentGraph.plotData are in internal format (cm^-1, linear)
            // They are only transformed for display via getGraphData() when rendering
            let xminTemp = plot.x.length > 0 ? Math.min(...plot.x) : null;
            const positiveXValues = plot.x.filter((x) => x > 0);
            let xminPosTemp =
              positiveXValues.length > 0 ? Math.min(...positiveXValues) : null;
            let xmaxTemp = plot.x.length > 0 ? Math.max(...plot.x) : null;
            if (xminTemp !== null && (xmin === null || xminTemp < xmin)) {
              xmin = xminTemp;
            }
            if (
              xminPosTemp !== null &&
              (xminPos === null || xminPosTemp < xminPos)
            ) {
              xminPos = xminPosTemp;
            }
            if (xmaxTemp !== null && (xmax === null || xmaxTemp > xmax)) {
              xmax = xmaxTemp;
            }
            if (yminTemp !== null && (ymin === null || yminTemp < ymin)) {
              ymin = yminTemp;
            }
            if (ymaxTemp !== null && (ymax === null || ymaxTemp > ymax)) {
              ymax = ymaxTemp;
            }
          }
          // if the y values are not null, we can use them to find the range
          if (ymin !== null && ymax !== null) {
            if (
              yminTotal === null ||
              ymaxTotal === null ||
              (yminTotal < ymax && ymaxTotal > ymin)
            ) {
              yAxisRange = {
                min: ymin,
                max: ymax,
              };
            } else if (
              yminTotal !== null &&
              ymaxTotal !== null &&
              ymin !== null &&
              ymax !== null
            ) {
              yAxisRange = {
                min: Math.min(yminTotal, ymin),
                max: Math.max(ymaxTotal, ymax),
              };
            }
          } else if (yminTotal !== null && ymaxTotal !== null) {
            yAxisRange = {
              min: yminTotal,
              max: ymaxTotal,
            };
          }

          let xmaxPos = xmax;
          if (xminPos === null) {
            xminPos = 0.1;
          }
          if (xmaxPos === null || xmaxPos < xminPos) {
            xmaxPos = 2 * xminPos;
          }
          if (xmin !== null && xmax !== null && xmin < xmax && xmin !== xmax) {
            if (graphLayout.xaxis.type !== "log") {
              xAxisRange = {
                min: xmin,
                max: xmax,
              };
            } else {
              xAxisRange = {
                min: xminPos,
                max: xmaxPos,
              };
            }
          }
        }
        let dX = (xAxisRange.max - xAxisRange.min) * 0.02;
        let dY = (yAxisRange.max - yAxisRange.min) * 0.02;
        let newXmin = xAxisRange.min - dX;
        if (newXmin < 0 && xAxisRange.min >= 0) {
          newXmin = 0;
        }
        let newXmax = xAxisRange.max + dX;
        if (newXmax > 0 && xAxisRange.max <= 0) {
          newXmax = 0;
        }
        let newYmin = yAxisRange.min - dY;
        let newYmax = yAxisRange.max + dY;
        if (isLogx) {
          dX =
            (normalToLog(xAxisRange.max) - normalToLog(xAxisRange.min)) * 0.02;
          newXmin = normalToLog(xAxisRange.min) - dX;
          newXmax = normalToLog(xAxisRange.max) + dX;
        }
        if (isLogy) {
          dY =
            (normalToLog(yAxisRange.max) - normalToLog(yAxisRange.min)) * 0.02;
          newYmin = logToNormal(normalToLog(yAxisRange.min) - dY);
          newYmax = logToNormal(normalToLog(yAxisRange.max) + dY);
        }
        xAxisRange = {
          min: newXmin,
          max: newXmax,
        };
        yAxisRange = {
          min: newYmin,
          max: newYmax,
        };
        xAxisRange.autorange = false;
        yAxisRange.autorange = false;
      } else if (hasProperty(eventData, "xaxis.range[0]")) {
        xAxisRange = {
          min: eventData["xaxis.range[0]"],
          max: eventData["xaxis.range[1]"],
          autorange: false,
        };
      } else if (hasProperty(eventData, "xaxis.range[1]")) {
        xAxisRange = {
          min: eventData["xaxis.range[0]"],
          max: eventData["xaxis.range[1]"],
          autorange: false,
        };
      } else if (hasProperty(eventData, "xaxis.range")) {
        xAxisRange = {
          min: eventData["xaxis.range"][0],
          max: eventData["xaxis.range"][1],
          autorange: false,
        };
      } else if (hasProperty(eventData, "yaxis.range[0]")) {
        yAxisRange = {
          min: eventData["yaxis.range[0]"],
          max: eventData["yaxis.range[1]"],
          autorange: false,
        };
      } else if (hasProperty(eventData, "yaxis.range[1]")) {
        yAxisRange = {
          min: eventData["yaxis.range[0]"],
          max: eventData["yaxis.range[1]"],
          autorange: false,
        };
      } else if (hasProperty(eventData, "yaxis.range")) {
        yAxisRange = {
          min: eventData["yaxis.range"][0],
          max: eventData["yaxis.range"][1],
          autorange: false,
        };
      }
      // We need to check this, because sometimes only y axis can be updated and we don't need to do anything
      // when that happens
      if (
        xAxisRange &&
        (hasProperty(xAxisRange, "min") || hasProperty(xAxisRange, "max"))
      ) {
        // Convert from Plotly's format back to internal format (cm^-1, linear)
        let currentLayout = deepCopy(graphLayout);

        // let convertedMin = xAxisRange.min;
        // let convertedMax = xAxisRange.max;

        // Create an xAxis-like object for handleRangesUpdate
        // handleRangesUpdate expects an object with range array, unit, and type propertiesc
        if (xAxisRange.min === null || xAxisRange.min === undefined) {
          xAxisRange.min = graphLayout.xaxis.range[0];
        }
        if (xAxisRange.max === null || xAxisRange.max === undefined) {
          xAxisRange.max = graphLayout.xaxis.range[1];
        }
        currentLayout.xaxis.range = [xAxisRange.min, xAxisRange.max];

        // convertedMin = yAxisRange.min;
        // convertedMax = yAxisRange.max;

        if (yAxisRange.min === null || yAxisRange.min === undefined) {
          yAxisRange.min = graphLayout.yaxis.range[0];
        }
        if (yAxisRange.max === null || yAxisRange.max === undefined) {
          yAxisRange.max = graphLayout.yaxis.range[1];
        }

        currentLayout.yaxis.range = [yAxisRange.min, yAxisRange.max];

        if (hasProperty(xAxisRange, "autorange")) {
          currentLayout.xaxis.autorange = xAxisRange.autorange;
        }
        if (hasProperty(yAxisRange, "autorange")) {
          currentLayout.yaxis.autorange = yAxisRange.autorange;
        }
        handleRangesUpdate(currentLayout);
      }
    } catch (error) {
      recordedErrorLog("Relayout handler failure: ", error);
    }
  };

  function handleRangesUpdate(layout) {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id == id);

      const layoutCopy = deepCopy(graphLayout);

      // Build xaxis update object
      const xaxisUpdate = {
        ...layoutCopy.xaxis,
        range: [layout.xaxis.range[0], layout.xaxis.range[1]],
      };
      // Only update autorange if it exists in the layout
      if (hasProperty(layout.xaxis, "autorange")) {
        xaxisUpdate.autorange = layout.xaxis.autorange;
      }

      // Build yaxis update object
      const yaxisUpdate = {
        ...layoutCopy.yaxis,
        range: [layout.yaxis.range[0], layout.yaxis.range[1]],
      };
      // Only update autorange if it exists in the layout
      if (hasProperty(layout.yaxis, "autorange")) {
        yaxisUpdate.autorange = layout.yaxis.autorange;
      }

      currentGraph.layout = {
        ...currentGraph.layout,
        xaxis: xaxisUpdate,
        yaxis: yaxisUpdate,
      };
      setGraphs((old) =>
        old.map((oldGraph) => {
          if (oldGraph.id === id) {
            return currentGraph;
          } else {
            return oldGraph;
          }
        })
      );
      setGraphLayout({
        ...layoutCopy,
        xaxis: xaxisUpdate,
        yaxis: yaxisUpdate,
      });

      const graphModels = currentGraph.containedModels;

      if (graphModels.length > 0) {
        const justModels = graphModels.map((modelQuant) => modelQuant.modelId);

        const modelsToUpdate = getModelsByIds(justModels, modelData);

        let rangesToSend = {};

        for (let i = 0; i < graphModels.length; i++) {
          const quantityModelSet = graphModels[i];

          const unitX = layout.xaxis.unit || units[0];
          const inverseUnit = isInverseUnit(unitX);

          const originalXUnit = getQuantityXUnitFromModels(modelData, quantityModelSet.modelId, quantityModelSet.quantity);

          const isInverseOriginalXUnit = isInverseUnit(originalXUnit);
          const ranges = convertRangeRequestFromUnitsToCm1(
            unitX,
            [layout.xaxis.range[0], layout.xaxis.range[1]],
            1000,
            originalXUnit
          );
          let rangeTemp = {
            min: ranges[0].min,
            max: ranges[0].max,
            nPoints: ranges[0].nPoints,
            isInverseUnit: !(inverseUnit === isInverseOriginalXUnit),
            isLogarithmic: layout.xaxis.type === "log",
            graphId: id,
          };
          if (ranges.length === 2) {
            rangeTemp = {
              ...rangeTemp,
              min: ranges[1].min,
              max: ranges[1].max,
              nPoints: ranges[1].nPoints,
              minNeg: ranges[0].min,
              maxNeg: ranges[0].max,
              nPointsNeg: ranges[0].nPoints,
            };
          }

          if (hasProperty(rangesToSend, quantityModelSet.modelId)) {
            if (
              hasProperty(
                rangesToSend[quantityModelSet.modelId],
                quantityModelSet.quantity
              )
            ) {
              rangesToSend = {
                ...rangesToSend,
                [quantityModelSet.modelId]: {
                  [quantityModelSet.quantity]: [
                    ...rangesToSend[quantityModelSet.modelId][
                      quantityModelSet.quantity
                    ],
                    rangeTemp,
                  ],
                },
              };
            } else {
              rangesToSend = {
                ...rangesToSend,
                [quantityModelSet.modelId]: {
                  ...rangesToSend[quantityModelSet.modelId],
                  [quantityModelSet.quantity]: [rangeTemp],
                },
              };
            }
          } else {
            rangesToSend = {
              ...rangesToSend,
              [quantityModelSet.modelId]: {
                [quantityModelSet.quantity]: [rangeTemp],
              },
            };
          }
        }

        const payloadForModelUpdate = createModelDistPayload(
          modelsToUpdate,
          rangesToSend,
          graphModels,
          currentUser,
          uploadedFiles
        );

        sendJsonMessage(payloadForModelUpdate, {
          type: "model-curve-update",
          modelsToUpdate: graphModels,
          graphId: id,
        });
      }
    } catch (error) {
      recordedErrorLog("Error handling ranges update:", error);
    }
  }

  // Custom Plot legend intereactions
  const handleLegendClick = (event) => {
    try {
      if (hasProperty(event.data[event.curveNumber], "modelId")) {
        if (!parametersOpen) {
          // setSectionsStatus((old) => {
          //   return {
          //     ...old,
          //     paramOpen: true,
          //   };
          // });
          setParametersOpen(true);
          // setShowLeftPanel(false);
          setCallFromGraph(true);
        }
      } else {
        let selectedFile = {};
        let selectedFileIndex = -1;

        for (let i = 0; i < uploadedFiles.length; i++) {
          const file = uploadedFiles[i];
          if (file.ID === event.data[event.curveNumber].fileId) {
            selectedFile = file;
            selectedFileIndex = i;
          }
        }

        setFileCallFromGraph(true);
        setSelectedFiles([{ file: selectedFile, index: selectedFileIndex }]);
      }

      // Return false to prevent Plotly's default legend click behavior
      return false;
    } catch (error) {
      recordedErrorLog("Legend click handler failure: ", error);
    }
  };

  const handleTitleClick = () => {
    try {
      const newTitle = prompt("Enter a new graph title", localTitle);
      if (newTitle !== null) {
        const currentGraph = graphs.find(
          (graphToFind) => graphToFind.id === id
        );
        currentGraph.title = newTitle;
        setLocalTitle(newTitle);
        setGraphs((old) =>
          old.map((oldGraph) => {
            if (oldGraph.id === id) {
              return currentGraph;
            } else {
              return oldGraph;
            }
          })
        );
      }
    } catch (error) {
      recordedErrorLog("Title click handler failure: ", error);
    }
  };

  useEffect(() => {
    if (localTitle) {
      setMinimisedWidth(useInputSize(localTitle) + 100);
    }
  }, [localTitle]);

  const handleMinimise = () => {
    setMinimisedWidth(useInputSize(localTitle) + 100);
    setIsExpanded(false);
  };

  const handleMaximise = () => {
    setIsExpanded(true);
  };

  const handleCurvesExport = () => {
    setExportModalIsOpen(true);
    monitorStateChange("export_curves_opened", {
      graphId: id,
    });
  };

  const handleExportSelectedCurves = (
    selectedCurveKeys,
    curveExportNames = {},
    sectionPrefixToggle = {},
    curveStatsState = {},
    curveLogGridState = {},
    curveUnitState = {}
  ) => {
    try {
      // Convert selected curve keys to a Set for quick lookup
      const groupedCurves = {};
      const curvesNeedingRequest = [];
      const exportMetadata = {};

      for (let i = 0; i < graphData.length; i++) {
        const graphCurveEntry = graphData[i];
        const legendGroup = graphCurveEntry.legendgroup || graphCurveEntry.name;

        // Build the key for this curve to check if it's selected
        let curveKey = "";
        let sectionKey = "";
        if (hasProperty(graphCurveEntry, "modelId")) {
          curveKey = `model-${graphCurveEntry.modelId}-${graphCurveEntry.quantity}`;
          sectionKey = "model-curves";
        } else if (hasProperty(graphCurveEntry, "fileId")) {
          curveKey = `file-${graphCurveEntry.fileId}`;
          sectionKey = "data-curves";
        } else if (hasProperty(graphCurveEntry, "variableId")) {
          curveKey = `variable-${graphCurveEntry.variableId}`;
          sectionKey = "parameter-tracking";
        }

        // Only process if this curve is selected
        if (!selectedCurveKeys.has(curveKey)) {
          continue;
        }

        // For model curves, check if stats match - skip if they don't match
        if (hasProperty(graphCurveEntry, "modelId")) {
          // Get current stats from graph
          const currentUnit = graphLayout?.xaxis?.unit || units[0];
          const convertedData = convertDataXYFromUnitsToUnits(
            units[0],
            currentUnit,
            graphCurveEntry.x,
            graphCurveEntry.y,
            true
          );

          const xValues = convertedData[0];
          const currentStats = {
            min: Math.min(...xValues),
            max: Math.max(...xValues),
            nPoints: xValues.length,
          };
          const exportStats = curveStatsState[curveKey];

          if (exportStats) {
            // Compare stats
            const exportMin =
              typeof exportStats.min === "string"
                ? parseFloat(exportStats.min)
                : exportStats.min;
            const exportMax =
              typeof exportStats.max === "string"
                ? parseFloat(exportStats.max)
                : exportStats.max;
            const exportNPoints =
              typeof exportStats.nPoints === "string"
                ? parseInt(exportStats.nPoints)
                : exportStats.nPoints;

            const isLog = curveLogGridState[curveKey] || false;
            const curveUnit =
              curveUnitState[curveKey] || graphLayout?.xaxis?.unit || units[0];
            const isInverse = isInverseUnit(curveUnit);

            // Compare units by value property (they are objects)
            const graphUnit = graphLayout?.xaxis?.unit || units[0];
            const curveUnitValue = curveUnit?.value || curveUnit;
            const graphUnitValue = graphUnit?.value || graphUnit;

            const statsMatch =
              !isNaN(currentStats.min) &&
              !isNaN(currentStats.max) &&
              !isNaN(exportMin) &&
              !isNaN(exportMax) &&
              currentStats.min === exportMin &&
              currentStats.max === exportMax &&
              currentStats.nPoints === 1000 &&
              exportNPoints === 1000 &&
              isLog === (graphLayout?.xaxis?.type === "log") &&
              curveUnitValue === graphUnitValue;

            if (!statsMatch) {
              // Stats don't match, need to request from backend
              // First determine filename for this curve
              const mode = sectionPrefixToggle[sectionKey] || "prefix";
              const exportName = curveExportNames[curveKey] || "";

              // Remove .txt or .dat extension from default name if present
              let defaultName = graphCurveEntry.name;
              if (defaultName.endsWith(".txt")) {
                defaultName = defaultName.slice(0, -4);
              } else if (defaultName.endsWith(".dat")) {
                defaultName = defaultName.slice(0, -4);
              }
              // Remove all whitespace from defaultName
              defaultName = defaultName.replace(/\s+/g, "");

              let fileName = defaultName;
              if (mode === "export name" && exportName.trim() !== "") {
                fileName = exportName.trim();
              } else if (mode === "prefix" && exportName.trim() !== "") {
                fileName = `${exportName.trim()}${defaultName}`;
              }

              curvesNeedingRequest.push({
                modelId: graphCurveEntry.modelId,
                quantity: graphCurveEntry.quantity,
                min: exportMin,
                max: exportMax,
                nPoints: exportNPoints,
                isLogarithmic: isLog,
                isInverseUnit: isInverse,
                unit: curveUnit,
              });

              // Store export metadata for this curve
              const metadataKey = `${graphCurveEntry.modelId}-${graphCurveEntry.quantity}`;
              exportMetadata[metadataKey] = {
                fileName: fileName,
                name: graphCurveEntry.name,
                unit: curveUnit,
                legendGroup: legendGroup,
              };
              continue;
            }
          }
        }

        if (!groupedCurves[legendGroup]) {
          // Determine the filename based on export name, prefix, or default
          const mode = sectionPrefixToggle[sectionKey] || "prefix";
          const exportName = curveExportNames[curveKey] || "";

          // Remove .txt or .dat extension from default name if present
          let defaultName = graphCurveEntry.name;
          if (defaultName.endsWith(".txt")) {
            defaultName = defaultName.slice(0, -4);
          } else if (defaultName.endsWith(".dat")) {
            defaultName = defaultName.slice(0, -4);
          }
          // Remove all whitespace from defaultName
          defaultName = defaultName.replace(/\s+/g, "");

          let fileName = defaultName;

          if (mode === "export name" && exportName.trim() !== "") {
            // Use export name, replacing the default name
            fileName = exportName.trim();
          } else if (mode === "prefix" && exportName.trim() !== "") {
            // Add prefix to the start of the default name (with extension removed)
            fileName = `${exportName.trim()}${defaultName}`;
          }
          // If neither prefix nor export name, use default name (with extension removed)

          // Get unit and quantity info for this curve
          let curveUnit = graphLayout?.xaxis?.unit || units[0];
          let quantityName = "";

          if (hasProperty(graphCurveEntry, "modelId")) {
            curveUnit = curveUnitState[curveKey] || curveUnit;
            quantityName =
              getQuantityNameFromModels(
                modelData,
                graphCurveEntry.modelId,
                graphCurveEntry.quantity
              ) || "";
          } else if (hasProperty(graphCurveEntry, "fileId")) {
            curveUnit = curveUnitState[curveKey] || curveUnit;
            // For data curves, quantity name is empty or could be derived from file
          } else if (hasProperty(graphCurveEntry, "variableId")) {
            curveUnit = null;
          }

          groupedCurves[legendGroup] = {
            name: graphCurveEntry.name,
            fileName: fileName,
            x: [],
            y: [],
            unit: curveUnit,
            quantityName: quantityName,
          };
        }

        // Add all points from this curve to the group
        groupedCurves[legendGroup].x.push(...graphCurveEntry.x);
        groupedCurves[legendGroup].y.push(...graphCurveEntry.y);

        // Update unit and quantity name if not set yet (for curves added to existing groups)
        if (hasProperty(graphCurveEntry, "modelId")) {
          if (
            !groupedCurves[legendGroup].unit ||
            groupedCurves[legendGroup].unit === graphLayout?.xaxis?.unit
          ) {
            const curveUnit =
              curveUnitState[curveKey] || graphLayout?.xaxis?.unit || units[0];
            groupedCurves[legendGroup].unit = curveUnit;
          }
          if (!groupedCurves[legendGroup].quantityName) {
            groupedCurves[legendGroup].quantityName =
              getQuantityNameFromModels(
                modelData,
                graphCurveEntry.modelId,
                graphCurveEntry.quantity
              ) || "";
          }
        } else if (hasProperty(graphCurveEntry, "fileId")) {
          if (
            !groupedCurves[legendGroup].unit ||
            groupedCurves[legendGroup].unit === graphLayout?.xaxis?.unit
          ) {
            const curveUnit =
              curveUnitState[curveKey] || graphLayout?.xaxis?.unit || units[0];
            groupedCurves[legendGroup].unit = curveUnit;
          }
        }
      }

      // Export one file per legend group
      Object.keys(groupedCurves).forEach((groupKey) => {
        const group = groupedCurves[groupKey];
        const exportUnit = group.unit || graphLayout?.xaxis?.unit || units[0];
        const quantityName = group.quantityName || "";

        // Sort the combined data by x values to maintain order
        const combinedData = group.x
          .map((x, index) => ({ x, y: group.y[index] }))
          .sort((a, b) => a.x - b.x);

        // Convert x values to the requested unit
        const convertedData = convertDataFromUnitsToUnits(
          units[0],
          exportUnit,
          combinedData,
          true
        );

        // Get unit value for header (always use .value property which is a string)
        let unitValue = exportUnit?.value || "cm^-1";

        if (group.unit === null) {
          unitValue = "";
        }
        // Convert quantity name to string if it's JSX
        let quantityNameStr = "";
        if (quantityName) {
          if (typeof quantityName === "string") {
            quantityNameStr = quantityName;
          } else {
            // It's JSX, convert to HTML string then extract text
            quantityNameStr = jsxToHtmlString(quantityName);
            // Remove HTML tags for plain text file
            quantityNameStr = quantityNameStr.replace(/<[^>]*>/g, "");
          }
        }

        // Create header row
        const headerRow = `${unitValue}\t${quantityNameStr}`;

        const stringifiedData = convertedData.map(
          (point) => `${point.x}\t${point.y}`
        );
        const singleString = [headerRow, ...stringifiedData].join("\n");

        downloadStringTxtFile(singleString, group.fileName);
      });

      // If there are curves needing backend request, create payload and send request
      if (curvesNeedingRequest.length > 0) {
        // Group by modelId for payload creation
        const modelsToRequest = {};
        const rangesForPayload = {};
        const quantityList = [];

        for (const curveReq of curvesNeedingRequest) {
          if (!modelsToRequest[curveReq.modelId]) {
            const model = getModelById(curveReq.modelId, modelData);
            if (model) {
              modelsToRequest[curveReq.modelId] = model;
            }
          }

          if (!rangesForPayload[curveReq.modelId]) {
            rangesForPayload[curveReq.modelId] = {};
          }
          if (!rangesForPayload[curveReq.modelId][curveReq.quantity]) {
            rangesForPayload[curveReq.modelId][curveReq.quantity] = [];
          }

          // Store range with npoints as an object (using array structure but adding nPoints)
          // The createModelDistPayload expects [min, max] arrays, but we can add nPoints property
          const originalXUnit = getQuantityXUnitFromModels(modelData, curveReq.modelId, curveReq.quantity);
          const isInverseOriginalXUnit = isInverseUnit(originalXUnit);
          const ranges = convertRangeRequestFromUnitsToCm1(
            curveReq.unit,
            [curveReq.min, curveReq.max],
            curveReq.nPoints,
            originalXUnit
          );

          let rangeEntry = {
            min: ranges[0].min,
            max: ranges[0].max,
            nPoints: ranges[0].nPoints,
            isInverseUnit:
              curveReq.isInverseUnit !== undefined
                ? !(curveReq.isInverseUnit === isInverseOriginalXUnit)
                : false,
            isLogarithmic:
              curveReq.isLogarithmic !== undefined
                ? curveReq.isLogarithmic
                : false,
          };
          if (ranges.length === 2) {
            rangeEntry = {
              ...rangeEntry,
              min: ranges[1].min,
              max: ranges[1].max,
              nPoints: ranges[1].nPoints,
              minNeg: ranges[0].min,
              maxNeg: ranges[0].max,
              nPointsNeg: ranges[0].nPoints,
            };
          }
          rangesForPayload[curveReq.modelId][curveReq.quantity].push(
            rangeEntry
          );

          // Add to quantityList if not already there
          if (
            !quantityList.find(
              (q) =>
                q.modelId === curveReq.modelId &&
                q.quantity === curveReq.quantity
            )
          ) {
            quantityList.push({
              modelId: curveReq.modelId,
              quantity: curveReq.quantity,
            });
          }
        }

        // Create payload with npoints
        const modelsArray = Object.values(modelsToRequest);
        const payload = createModelDistPayload(
          modelsArray,
          rangesForPayload,
          quantityList,
          currentUser,
          uploadedFiles
        );

        // Store export metadata in request details for later use
        sendJsonMessage(payload, {
          type: "export-model-curve-request",
          graphId: id,
          exportMetadata: exportMetadata,
        });
      }
    } catch (error) {
      recordedErrorLog("Error handling export model curve:", error);
    }
  };

  // Handler for export curve responses
  function handleExportModelCurveResponse(jsonMessage, details) {
    try {
      if (!details.exportMetadata) return;

      const groupedCurves = {};

      // Process each requested curve
      for (const curveKey in details.exportMetadata) {
        const metadata = details.exportMetadata[curveKey];
        const [modelIdStr, quantityStr] = curveKey.split("-");
        const modelId = parseInt(modelIdStr);
        const quantity = parseInt(quantityStr);

        // Find the curve data in response
        const foundData = jsonMessage.Model?.SendCurve?.find(
          (curveEntry) =>
            curveEntry.modelid == modelId &&
            curveEntry.curves?.[0]?.quantity == quantity
        );

        if (foundData && foundData.curves && foundData.curves.length > 0) {
          const coordinates = foundData.curves[0].coordinates;

          // Get unit and quantity name from metadata
          const exportUnit =
            metadata.unit || graphLayout?.xaxis?.unit || units[0];
          const quantityName =
            getQuantityNameFromModels(modelData, modelId, quantity) || "";

          const xUnit = getQuantityXUnitFromModels(modelData, modelId, quantity);
          const convertedData = convertDataFromUnitsToUnits(xUnit, units[0], coordinates, true);

          if (!groupedCurves[metadata.legendGroup]) {
            groupedCurves[metadata.legendGroup] = {
              name: metadata.name,
              fileName: metadata.fileName,
              x: [],
              y: [],
              unit: exportUnit,
              quantityName: quantityName,
            };
          }

          // Add coordinates to the group (coordinates from backend are in cm^-1)
          for (const point of convertedData) {
            groupedCurves[metadata.legendGroup].x.push(point.x);
            groupedCurves[metadata.legendGroup].y.push(point.y);
          }
        }
      }

      // Export all grouped curves
      Object.keys(groupedCurves).forEach((groupKey) => {
        const group = groupedCurves[groupKey];
        const exportUnit = group.unit || graphLayout?.xaxis?.unit || units[0];
        const quantityName = group.quantityName || "";

        // Sort the combined data by x values to maintain order
        const combinedData = group.x
          .map((x, index) => ({ x, y: group.y[index] }))
          .sort((a, b) => a.x - b.x);

        // Convert x values to the requested unit (coordinates from backend are in cm^-1)
        const convertedData = convertDataFromUnitsToUnits(
          units[0],
          exportUnit,
          combinedData,
          true
        );
        // Get unit value for header (always use .value property which is a string)
        const unitValue = exportUnit?.value || "cm^-1";

        // Convert quantity name to string if it's JSX
        let quantityNameStr = "";
        if (quantityName) {
          if (typeof quantityName === "string") {
            quantityNameStr = quantityName;
          } else {
            // It's JSX, convert to HTML string then extract text
            quantityNameStr = jsxToHtmlString(quantityName);
            // Remove HTML tags for plain text file
            quantityNameStr = quantityNameStr.replace(/<[^>]*>/g, "");
          }
        }

        // Create header row
        const headerRow = `${unitValue}\t${quantityNameStr}`;

        const stringifiedData = convertedData.map(
          (point) => `${point.x}\t${point.y}`
        );
        const singleString = [headerRow, ...stringifiedData].join("\n");

        downloadStringTxtFile(singleString, group.fileName);
      });
    } catch (error) {
      recordedErrorLog("Error handling export model curve response: ", error);
    }
  }

  return (
    <ResizableBox
      width={isExpanded ? currentSize.width : minimisedWidth}
      // height={extraSection ? currentSize.height + 40 : currentSize.height}
      height={isExpanded ? currentSize.height : 35}
      // minConstraints={[100, 100]}
      minConstraints={
        isExpanded
          ? [Math.max(window.innerWidth * 0.2, 270), window.innerHeight * 0.2]
          : [minimisedWidth, 35]
      }
      maxConstraints={[window.innerWidth * 0.7, window.innerHeight * 0.8]}
      style={{
        left: position.x,
        top: position.y,
        zIndex: zIndex + 105,
        position: "absolute",
      }}
      onResize={(event, { size }) => {
        if (isExpanded) {
          debouncedSetSize({ width: size.width, height: size.height });
        }
      }}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
      // onResize={(event, { size }) => {
      //   setCurrentSize((prevSize) => ({
      //     width: size.width,
      //     height: size.height,
      //   }));
      // }}
      resizeHandles={isExpanded ? ["se"] : []} // Conditional handles
      className="grapWindowResizableBox"
      onClick={() => onGraphClick(id)}
    >
      <div
        ref={preview}
        style={{
          // left: position.x,
          // top: position.y,
          // width: currentSize.width,
          // height: extraSection ? currentSize.height + 40 : currentSize.height,
          width: "100%",
          height: "100%",
          zIndex: zIndex + 100,
        }}
        className="draggableWindow"
        id="draggable-graph-window"
        onDragStart={() => onDragStart(id)}
        onDragEnd={onDragEnd}
        // {...props}
      >
        <div ref={drag} className="dragHandler">
          {isExpanded ? (
            <>
              <div className="dataIcons" ref={windowDataIcons}>
                <SettingsIcon
                  className="settingsIcon"
                  id="graph-settings-icon"
                  onClick={() => {
                    if (!isFitOngoing && !isLoopOngoing) {
                      handleSettings();
                    }
                  }}
                />
                <SsidChartIcon
                  className="curvesIcon"
                  id="graph-curves-icon"
                  onClick={() => {
                    if (!isFitOngoing && !isLoopOngoing) {
                      handleCurvesOptOpen();
                    }
                  }}
                />
                {extraRaman ? (
                  <div className="ramanExtras">
                    <CustomButton
                      text="AutoFit"
                      extraClassnames={["graphHeaderVersion"]}
                      handleClick={handleRamanAutofit}
                      id="autofit-raman-graph-button"
                      disabled={isFitOngoing || isLoopOngoing}
                    />
                    {/* <Button
                      variant="contained"
                      size="small"
                      sx={{ m: 1 }}
                      onClick={() => handleRamanAutofit()}
                      className="ramanAutofit"
                      id="autofit-raman-graph-button"
                      disabled={isFitOngoing || isLoopOngoing}
                    >
                      AutoFit
                    </Button> */}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </>
          ) : (
            <></>
          )}
          <div
            className="windowTitle"
            id="graph-window-title"
            onClick={() => handleTitleClick()}
          >
            {localTitle}
          </div>
          <div className="dragWinManageIcons" ref={windowIcons}>
            {isExpanded && graphData.length > 0 ? (
              // <Button
              //   variant="contained"
              //   size="small"
              //   sx={{ m: 1 }}
              //   onClick={() => handleCurvesExport()}
              //   className="export"
              //   disabled={isFitOngoing || isLoopOngoing}
              // >
              //   Export
              // </Button>

              <CustomButton
                text="Export"
                extraClassnames={["graphHeaderVersion"]}
                handleClick={() => handleCurvesExport()}
                disabled={isFitOngoing || isLoopOngoing}
              />
            ) : (
              <></>
            )}

            {isExpanded ? (
              <CloseFullscreenIcon
                className="sizeIcon"
                onClick={() => handleMinimise()}
                id="minimise-graph-window-icon"
              />
            ) : (
              <OpenInFullIcon
                className="sizeIcon"
                onClick={() => handleMaximise()}
                id="maximise-graph-window-icon"
              />
            )}
            <div className="deleteGraphButtonContainer">
              <CloseButton
                onClose={handleGraphDeleteIcon}
                customId="delete-graph-button"
              />
            </div>
            {/* <div
              className="deleteGraphButton"
              onClick={() => handleGraphDeleteIcon()}
              id="delete-graph-button"
            >
              <HighlightOffIcon className="deleteGraphIcon" />
            </div> */}
          </div>
        </div>
        {isExpanded ? (
          <>
            <div
              className="dragContainer"
              ref={autoFitRef}
              id="dragrable-graph-plot-container"
            >
              <div
                className="plotWrapper"
                style={{ width: "100%", height: "100%" }}
              >
                {isResizing ? (
                  <img
                    className="plotPlaceholder"
                    src={graphPlaceholder}
                    alt="Resizing ..."
                  />
                ) : checkAccessLevel(1) ? (
                  <Plot
                    data={graphDataForDisplay}
                    layout={graphLayoutForDisplay}
                    onRelayout={handleRelayout}
                    onLegendClick={handleLegendClick}
                  />
                ) : (
                  <Plot
                    data={graphDataForDisplay}
                    layout={graphLayoutForDisplay}
                    onRelayout={handleRelayout}
                    onLegendClick={handleLegendClick}
                    config={{ modeBarButtonsToRemove: ["toImage"] }}
                  />
                )}
              </div>
              <AutoFitModal
                model={ramanInGraph}
                changeModel={changeRamanInGraphModel}
                reference={autoFitRef}
                autofitModalIsOpen={autofitModalIsOpen}
                setAutofitModalIsOpen={setAutofitModalIsOpen}
                preSelectedFile={autoSelectedFile}
              />
            </div>
            <OptionsModal
              settingsModalIsOpen={settingsModalIsOpen}
              setSettingsModalIsOpen={setSettingsModalIsOpen}
              reference={windowDataIcons}
              initialLayout={initialLayout}
              graphRef={graphRef}
              id={id}
              updateGraphLayout={updateGraphLayout}
              zIndex={zIndex}
              setXAxisUpdated={setXAxisUpdatedInOptions}
              type={type}
            />
            <CurvesModal
              modelData={modelData}
              curvesModalIsOpen={curvesModalIsOpen}
              setCurvesModalIsOpen={setCurvesModalIsOpen}
              reference={windowDataIcons}
              distList={distList}
              handleDistDelete={handleDistDelete}
              selectFileList={selectFileList}
              selectModelList={selectModelList}
              graphRef={graphRef}
              id={id}
              updateGraphPlots={updateGraphPlots}
              zIndex={zIndex}
              type={type}
            />
            <ExportCurvesModal
              exportModalIsOpen={exportModalIsOpen}
              setExportModalIsOpen={setExportModalIsOpen}
              reference={windowDataIcons}
              onExport={handleExportSelectedCurves}
              zIndex={zIndex}
              graphsToInclude={[
                {
                  ...graphRef.current.find((graph) => graph.id == id),
                  plotData: graphData,
                  layout: graphLayout,
                },
              ]}
            />
          </>
        ) : (
          <></>
        )}
        <ConfirmationScreen
          onYes={() => handleGraphDelete()}
          onNo={() => setDelModalIsOpen(false)}
          text={"Are you sure you want to remove this graph?"}
          open={delModalIsOpen}
          setOpen={setDelModalIsOpen}
          element={windowIcons}
        />
      </div>
    </ResizableBox>
  );
}

export default DraggableGraphWindow;
